/*
 * Patches the native <predictive-search> custom element to use Algolia for
 * product results instead of Shopify's Section Rendering API. The algoliasearch
 * client is lazy-loaded on first keypress to avoid blocking pages that don't
 * need it. All other behaviour (open/close, loading state, click-outside) is
 * left intact because we only replace `renderSection`.
 *
 * Requires `window.algoliaConfig` (set by `algolia-config.liquid` in theme.liquid)
 * including the `_vendorUrl` key that points at the vendored algoliasearch-lite
 * asset so we can load it without hardcoding a CDN URL.
 */
(function () {
  'use strict';

  customElements.whenDefined('predictive-search').then(function () {
    var cfg = window.algoliaConfig;
    if (!cfg || !cfg.app_id || !cfg.search_api_key || !cfg.index_prefix || !cfg._vendorUrl) return;

    var PS = customElements.get('predictive-search');
    if (!PS) return;

    var baseIndex = cfg.index_prefix + 'products';
    var theme = window.AlgoliaTheme || {};
    var root = theme.rootUrl || '';

    // --- Client factory (lazy) ------------------------------------------
    var client = null;
    var clientPromise = null;

    // algoliasearch v5 lite exposes liteClient() on the window['algoliasearch/lite']
    // UMD namespace rather than the v4 global algoliasearch() function.
    function makeClient() {
      var ns = window['algoliasearch/lite'];
      return ns && typeof ns.liteClient === 'function'
        ? ns.liteClient(cfg.app_id, cfg.search_api_key)
        : null;
    }

    function getClient() {
      if (client) return Promise.resolve(client);
      // Already loaded by the Algolia section on collection/search pages.
      var existing = makeClient();
      if (existing) {
        client = existing;
        return Promise.resolve(client);
      }
      if (clientPromise) return clientPromise;
      clientPromise = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = cfg._vendorUrl;
        s.onload = function () {
          client = makeClient();
          if (!client) {
            return reject(new Error('[algolia-predictive] liteClient not defined after load'));
          }
          resolve(client);
        };
        s.onerror = reject;
        document.head.appendChild(s);
      });
      return clientPromise;
    }

    // --- Helpers (mirrors algolia-helpers.js subset) --------------------
    function esc(str) {
      return String(str == null ? '' : str)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    function money(major) {
      var fmt = theme.moneyFormat ||
                (window.FoxTheme && window.FoxTheme.settings && window.FoxTheme.settings.moneyFormat) ||
                '${{amount}}';
      var cents = Math.round(Number(major) * 100);
      var formatted = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return fmt.replace(/\{\{\s*amount[^}]*\}\}/, formatted);
    }

    // Algolia returns `_highlightResult.<attr>.value` already HTML-escaped with
    // the configured pre/post tags around matches — safe to inject as innerHTML.
    // Falls back to the manually-escaped raw value when no highlight is present.
    function highlight(hit, attr) {
      var hr = hit._highlightResult && hit._highlightResult[attr];
      return hr && typeof hr.value === 'string' ? hr.value : esc(hit[attr] || '');
    }

    // District-style price: current price, with the original struck through when
    // on sale. Mirrors the source's `aa-ItemContentPrice` row.
    function priceHTML(hit) {
      // Indexed prices are shop currency; on another market leave a placeholder
      // that `fillLocalizedPrices` fills from the market's own product JSON.
      if (theme.localizedPricing) {
        return '<div class="aa-ItemContentPrice" data-algolia-price data-handle="' + esc(hit.handle) + '"></div>';
      }
      // Varying prices get the same refill: the index can't say which
      // compare-at belongs with the lowest price.
      var p = theme.hitPrice(hit);
      var html = priceMarkup(p.price, p.compare_at_price, p.varies);
      if (!p.varies) return html;
      return html.replace('<div class="aa-ItemContentPrice"', '<div class="aa-ItemContentPrice" data-algolia-price data-handle="' + esc(hit.handle) + '"');
    }

    function fillLocalizedPrices(container) {
      container.querySelectorAll('[data-algolia-price]').forEach(function (el) {
        el.removeAttribute('data-algolia-price');
        var handle = el.getAttribute('data-handle');
        theme.localizedProduct(handle).then(function (product) {
          var v = theme.localizedProductPrice(product);
          if (!el.isConnected) return;
          theme.linkToVariant(el.closest('a'), handle, v.variant);
          el.outerHTML = priceMarkup(v.price / 100, v.compare_at_price / 100, v.varies);
        }).catch(function () {
          // Keep what's there: blank rather than the wrong currency, or the indexed "From" price.
        });
      });
    }

    function priceMarkup(p, c, varies) {
      var sale = c > p;
      return (
        '<div class="aa-ItemContentPrice">' +
          '<div class="aa-ItemContentPriceCurrent' + (sale ? ' aa-ItemContentPriceCurrent--sale' : '') + '">' +
            theme.fromPrice(money(p), varies) +
          '</div>' +
          (sale ? '<div class="aa-ItemContentPriceDiscounted"><s>' + money(c) + '</s></div>' : '') +
        '</div>'
      );
    }

    // Left-rail line icons, lifted verbatim from District's collection / article
    // / page autocomplete templates.
    var ICONS = {
      collections: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/></svg>',
      articles: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/></svg>',
      pages: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>'
    };

    // One right-panel product card (image on top, title + price below).
    function productCard(hit, index, queryID) {
      var titleHtml = highlight(hit, 'title');
      var alt = esc(hit.title || '');
      var url = theme.productUrl(hit.handle);
      var img = hit.product_image || hit.image || '';
      var imgTag = img
        ? '<img src="' + esc(img + (img.indexOf('?') === -1 ? '?width=320' : '&width=320')) +
          '" alt="' + alt + '" loading="lazy" width="160" height="160">'
        : '';
      // Algolia coordinates for click/conversion insights, read by the delegated
      // listener in algolia-insights.js. queryID only present with clickAnalytics.
      var insightsAttrs = '';
      if (hit.objectID != null) insightsAttrs += ' data-algolia-objectid="' + esc(String(hit.objectID)) + '"';
      if (queryID) insightsAttrs += ' data-algolia-queryid="' + esc(String(queryID)) + '"';
      insightsAttrs += ' data-algolia-position="' + (index + 1) + '"';
      return (
        '<a class="aa-ItemLink aa-ProductItem" href="' + url + '"' + insightsAttrs + '>' +
          '<div class="aa-ItemContent">' +
            '<div class="aa-ItemPicture aa-ItemPicture--loaded">' + imgTag + '</div>' +
            '<div class="aa-ItemContentBody">' +
              '<div class="aa-ItemContentTitleWrapper">' +
                '<div class="aa-ItemContentTitle">' + titleHtml + '</div>' +
              '</div>' +
              priceHTML(hit) +
            '</div>' +
          '</div>' +
        '</a>'
      );
    }

    // One left-rail item (icon + highlighted title) for collections/articles/pages.
    function railItem(hit, href, iconKey) {
      return (
        '<a class="aa-ItemLink" href="' + esc(href) + '">' +
          '<div class="aa-ItemWrapper"><div class="aa-ItemContent">' +
            '<div class="aa-ItemIcon aa-ItemIcon--noBorder">' + ICONS[iconKey] + '</div>' +
            '<div class="aa-ItemContentBody">' +
              '<div class="aa-ItemContentTitle">' + highlight(hit, 'title') + '</div>' +
            '</div>' +
          '</div></div>' +
        '</a>'
      );
    }

    function sourceHeader(title) {
      return (
        '<div class="aa-SourceHeader">' +
          '<span class="aa-SourceHeaderTitle">' + esc(title) + '</span>' +
          '<div class="aa-SourceHeaderLine"></div>' +
        '</div>'
      );
    }

    // A left-rail source group; empty hit lists render nothing.
    function railSource(title, hits, hrefFor, iconKey) {
      if (!hits || !hits.length) return '';
      var rows = hits.map(function (hit) { return railItem(hit, hrefFor(hit), iconKey); }).join('');
      return (
        '<section class="aa-Source">' + sourceHeader(title) +
          '<div class="aa-List">' + rows + '</div>' +
        '</section>'
      );
    }

    function collectionHref(hit) { return root + '/collections/' + encodeURIComponent(hit.handle || ''); }
    function pageHref(hit) { return root + '/pages/' + encodeURIComponent(hit.handle || ''); }
    function articleHref(hit) {
      var blog = hit.blog && hit.blog.handle;
      return blog ? root + '/blogs/' + encodeURIComponent(blog) + '/' + encodeURIComponent(hit.handle || '') : (hit.url || '#');
    }

    function buildHTML(query, data) {
      var products = data.products || { hits: [], queryID: undefined };
      var hasLeft = (data.collections && data.collections.length) ||
                    (data.articles && data.articles.length) ||
                    (data.pages && data.pages.length);

      if (!products.hits.length && !hasLeft) {
        return (
          '<div class="aa-PanelLayout aa-NoResults">' +
            '<p class="aa-NoResultsHeader">No results for <q>' + esc(query) + '</q></p>' +
            '<p class="m-0 text-subtext">Try checking your spelling or use more general terms.</p>' +
          '</div>'
        );
      }

      var productCards = products.hits.map(function (hit, i) {
        return productCard(hit, i, products.queryID);
      }).join('');
      var productsSection =
        '<section class="aa-Source aa-Source--products">' +
          sourceHeader(query ? 'Products for ' + query : 'Products') +
          '<div id="predictive-search-results-products-list" class="aa-List aa-ProductGrid" role="listbox">' +
            productCards +
          '</div>' +
        '</section>';

      // Products-only when nothing matched in the left-rail sources.
      if (!hasLeft) {
        return (
          '<div class="aa-PanelLayout aa-Panel--scrollable">' +
            '<div class="aa-PanelSection">' + productsSection + '</div>' +
          '</div>'
        );
      }

      var left =
        railSource('Collections', data.collections, collectionHref, 'collections') +
        railSource('Articles', data.articles, articleHref, 'articles') +
        railSource('Pages', data.pages, pageHref, 'pages');

      return (
        '<div class="aa-PanelLayout aa-Panel--scrollable">' +
          '<div class="aa-PanelSections">' +
            '<div class="aa-PanelSection--left">' + left + '</div>' +
            '<div class="aa-PanelSection--right">' + productsSection + '</div>' +
          '</div>' +
        '</div>'
      );
    }

    // --- Patch ----------------------------------------------------------
    var origRenderSection = PS.prototype.renderSection;

    PS.prototype.renderSection = function (url) {
      var self = this;
      var query = this.getQuery();

      // No query — let the original handle showing the recommendations panel.
      if (!query) {
        if (origRenderSection) origRenderSection.call(this, url);
        return;
      }

      self.setLoadingState(true);

      var stockFilter = cfg.stock_policy === 'deny'
        ? 'inventory_quantity > 0'
        : cfg.stock_policy === 'continue' ? 'inventory_available:true' : '';

      var hl = { highlightPreTag: '<mark>', highlightPostTag: '</mark>' };

      // One multi-index search: products + (optionally) collections/articles/pages,
      // gated on the same `index_*` flags the District integration uses. The order
      // here is the order we read results back in `sources` below.
      var queries = [{
        indexName: baseIndex,
        params: {
          query: query,
          hitsPerPage: cfg.products_autocomplete_hits_per_page || 8,
          distinct: Boolean(cfg.show_products),
          clickAnalytics: Boolean(cfg.analytics_enabled),
          filters: stockFilter || undefined,
          highlightPreTag: hl.highlightPreTag,
          highlightPostTag: hl.highlightPostTag,
          attributesToRetrieve: [
            'title', 'handle', 'price', 'compare_at_price',
            'variants_min_price', 'variants_max_price',
            'variants_compare_at_price_min', 'variants_compare_at_price_max',
            'product_image', 'image'
          ]
        }
      }];
      var sources = ['products'];

      function addSource(name, suffix, perPage) {
        queries.push({
          indexName: cfg.index_prefix + suffix,
          params: {
            query: query,
            hitsPerPage: perPage,
            clickAnalytics: Boolean(cfg.analytics_enabled),
            highlightPreTag: hl.highlightPreTag,
            highlightPostTag: hl.highlightPostTag
          }
        });
        sources.push(name);
      }

      if (cfg.index_collections) addSource('collections', 'collections', cfg.collections_autocomplete_hits_per_page || 2);
      if (cfg.index_articles) addSource('articles', 'articles', cfg.articles_autocomplete_hits_per_page || 2);
      if (cfg.index_pages) addSource('pages', 'pages', cfg.pages_autocomplete_hits_per_page || 2);

      getClient()
        .then(function (c) { return c.search(queries); })
        .then(function (response) {
          var results = response.results || [];
          var data = { products: { hits: [], queryID: undefined } };
          sources.forEach(function (name, i) {
            var r = results[i] || {};
            if (name === 'products') {
              data.products = { hits: r.hits || [], queryID: r.queryID };
            } else {
              data[name] = r.hits || [];
            }
          });

          var container = self.querySelector('[id^="PredictiveSearchResults-"]');
          if (!container) return;

          container.innerHTML = buildHTML(query, data);
          fillLocalizedPrices(container);
          self.setLoadingState(false);

          self.searchContent && self.searchContent.classList.remove('hidden');
          self.setAttribute('results', 'true');

          if (self.isHeaderSearch && !document.body.classList.contains('search-open')) {
            document.body.classList.add('search-open');
            self.toggleNavButtonVisibility && self.toggleNavButtonVisibility(true);
          }
          self.classList.add('predictive-search-open');
        })
        .catch(function () {
          // Algolia unavailable — fall back to Shopify's predictive search.
          self.setLoadingState(false);
          if (origRenderSection) origRenderSection.call(self, url);
        });
    };
  });
})();
